import { cloneElement, isValidElement, useCallback } from 'react';
import { omit } from 'lodash';

import { useBoolean } from '@blockworks/platform/hooks';
import { useTrack } from '@blockworks/platform/services/analytics';
import { Bit } from '@blockworks/ui/bit';
import { ExpandIcon, XCloseIcon } from '@blockworks/ui/icon';
import { Modal } from '@blockworks/ui/modal';
import { Popover } from '@blockworks/ui/popover';

import { AnalyticsEvent } from '../../../../utils/events-tracking';
import { FeatureControlButton } from '../../../feature-control-button';
import { SubscribeToAccess } from '../../../subscription-gates';
import type { AnalyticsTrackingFallbackProps } from '../../models';
import { VisualizationRendererProps } from '../visualization-renderer/visualization-renderer.model';

type ExpandVisualizationProps =
    | ({
          isGated: true;
          visualization?: VisualizationRendererProps['visualization'];
          children?: React.ReactNode;
      } & Pick<AnalyticsTrackingFallbackProps, 'trackVisualizationFallback'>)
    | {
          isGated?: false;
          visualization: VisualizationRendererProps['visualization'];
          children: React.ReactNode;
          trackVisualizationFallback?: never;
      };

const ExpandVisualization = ({
    children,
    visualization,
    isGated,
    trackVisualizationFallback,
}: ExpandVisualizationProps) => {
    const modal = useBoolean(false);
    const track = useTrack();
    const onClick = useCallback(() => {
        modal.setTrue();
        track?.(`${visualization?.type ?? 'Visualization'} > ${AnalyticsEvent.ExpandButtonClicked}`, {
            visualization: omit(visualization ?? trackVisualizationFallback, ['execution', 'config']),
        });
    }, [modal, track, visualization, trackVisualizationFallback]);

    const expandedVisualizationProps = {
        componentSlots: {
            HeaderEnd: () => (
                <FeatureControlButton
                    onClick={modal.setFalse}
                    aria-label="Close Modal"
                    intent="neutral"
                    icon={XCloseIcon}
                />
            ),
        },
    };

    if (isGated)
        return (
            <Popover trigger={<FeatureControlButton intent={'primary-soft'} onClick={onClick} icon={ExpandIcon} />}>
                <SubscribeToAccess
                    trackEvent={{ event: AnalyticsEvent.SubscribeToExpandClicked, data: { visualization } }}
                >
                    To view this expanded chart, sign up or log in to Research.
                </SubscribeToAccess>
            </Popover>
        );

    return (
        <>
            <FeatureControlButton intent={'primary-soft'} onClick={onClick} icon={ExpandIcon} />
            {children && (
                <Modal
                    open={modal.value}
                    onOpenChange={modal.setValue}
                    onClose={() => modal.setFalse()}
                    classNames={{ base: 'w-[calc(100vw-1rem)] h-[calc(100vh-1rem)]' }}
                >
                    <Modal.Content>
                        <Modal.Title visuallyHidden>Expand Visualization</Modal.Title>
                        <Bit.div overflow="hidden" className="w-[calc(100vw-1rem)] h-[calc(100vh-1rem)]">
                            {isValidElement(children)
                                ? cloneElement(children, expandedVisualizationProps as {})
                                : children}
                        </Bit.div>
                    </Modal.Content>
                </Modal>
            )}
        </>
    );
};

export { ExpandVisualization };
